import { useMemo } from 'react';
import { pick, prop } from 'ramda';
import { useAppSelector } from 'js/store/hooks';
import { FORM_FIELD_TYPE } from 'js/components/common/Form/constants';

import * as helpers from '../helpers';
import { useGetFieldId } from './useGetFieldId';
import { useDynamicReducerSelector } from '../store';
import { DEFAULT_VALUE_FOR_HIDE_FROM_USER_TYPES, SELECT_ALL_SUFFIX } from '../constants';

export const useDefaultValues = () => {
    const fields = useDynamicReducerSelector(prop('fields'));
    const { allowedCurrencies, allowedCountries, betLossLimitActions } = useAppSelector(pick([
        'allowedCurrencies', 'allowedCountries', 'betLossLimitActions'
    ]));
    const getFieldId = useGetFieldId();

    return useMemo(() => fields
        .filter((field) => (field.imsField || field.usePlayerTags || field.isNoIMSField) && field.defaultValue
            && (field.hideFromUser || !DEFAULT_VALUE_FOR_HIDE_FROM_USER_TYPES.includes(field.type))
            || field.type === FORM_FIELD_TYPE.CHECKBOX
            || field.type === FORM_FIELD_TYPE.DROPDOWN
            || field.type === FORM_FIELD_TYPE.COUNTRY
            || field.type === FORM_FIELD_TYPE.RADIO_BUTTON
            || field.type === FORM_FIELD_TYPE.PHONE
            || field.type === FORM_FIELD_TYPE.CELLPHONE
            || field.type === FORM_FIELD_TYPE.BET_LIMITS
            || field.type === FORM_FIELD_TYPE.LOSS_LIMITS
            || field.type === FORM_FIELD_TYPE.DEPOSIT_LIMITS
            || field.type === FORM_FIELD_TYPE.CONTACT_PREFERENCES)
        .reduce((accumulator, field) => {
            const value = (() => {
                switch (field.type) {
                    case FORM_FIELD_TYPE.RADIO_BUTTON:
                        return helpers.imsToFieldValue(field.defaultValue, field);
                    case FORM_FIELD_TYPE.DROPDOWN:
                    case FORM_FIELD_TYPE.COUNTRY:
                        return helpers.imsToFieldValue(field.defaultValue, field, { allowedCurrencies, allowedCountries });
                    case FORM_FIELD_TYPE.CONTACT_PREFERENCES:
                        return false;
                    default: return field.defaultValue;
                }
            })();

            if (value) {
                accumulator[field.name] = value;
            }

            if ([FORM_FIELD_TYPE.PHONE, FORM_FIELD_TYPE.CELLPHONE].includes(field.type)) {
                accumulator[`${field.name}-areaCode`] = field.phoneCode;
            }

            // region Limits
            if ([FORM_FIELD_TYPE.DEPOSIT_LIMITS, FORM_FIELD_TYPE.BET_LIMITS, FORM_FIELD_TYPE.LOSS_LIMITS].includes(field.type)) {
                accumulator[field.imsFieldDaily] = '0';
                accumulator[field.imsFieldWeekly] = '0';
                accumulator[field.imsFieldMonthly] = '0';
            }

            if (field.type === FORM_FIELD_TYPE.BET_LIMITS) {
                accumulator.betlimitaction = helpers.getLimitActions(betLossLimitActions.betlimitActions)[0] || '';
                accumulator[field.imsFieldSession] = '0';
            }

            if (field.type === FORM_FIELD_TYPE.LOSS_LIMITS) {
                accumulator.losslimitaction = helpers.getLimitActions(betLossLimitActions.losslimitActions)[0] || '';
                accumulator[field.imsFieldSession] = '0';
            }
            // endregion

            // region Contact Preferences
            if (field.type === FORM_FIELD_TYPE.CONTACT_PREFERENCES) {
                const prefFieldId = `${getFieldId(field.id)}_${field.name}`;

                if (!Array.isArray(field.contactPreferences)) {
                    accumulator[prefFieldId] = field.defaultValue;

                    return accumulator;
                }

                field.contactPreferences.forEach((pref) => {
                    if (field.enableSelectAll) {
                        accumulator[`${prefFieldId}${SELECT_ALL_SUFFIX}`] = field.defaultValue;
                    }

                    if (pref?.products?.length) {
                        accumulator[`${prefFieldId}-${pref.channel}${SELECT_ALL_SUFFIX}`] = field.defaultValue;

                        pref.products.forEach((product) => accumulator[`${prefFieldId}-${pref.channel}-${product}`] = field.defaultValue);

                        return accumulator;
                    }

                    accumulator[`${prefFieldId}-${pref.channel}`] = field.defaultValue;
                }, {});
            }
            // endregion

            return accumulator;
        }, {}), [fields, allowedCurrencies, allowedCountries, betLossLimitActions, getFieldId]);
};
